/**
 * RepairTestHarnessPage — Landlord Portal
 *
 * Route: /repairs/:id/test-harness
 *
 * Three-column actor harness for exercising repair ticket messaging.
 * Each column sends via the channel-inbound path so the ROUTER classifies the message, exactly
 * as in production. Agent replies about other cases are hidden; each inbound message shows
 * where the router sent it. Channel messages are polled every 2s.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getHarnessActors,
  type HarnessActor,
  type HarnessActors,
} from '../api/repairs.api';
import {
  postChannelInbound,
  getChannelMessages,
  type HarnessChannelMessage,
} from '../api/channel-inbound.api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Actor types used in harness columns
type ColumnActor = 'tenant' | 'manager' | 'vendor';

interface ColumnConfig {
  actorType: ColumnActor;
  label: string;
  roleBadgeClass: string;
  actor: HarnessActor | null;
}

const SENDER_BADGE: Record<string, string> = {
  tenant: 'bg-info/15 text-info',
  manager: 'bg-primary/15 text-primary',
  vendor: 'bg-success/15 text-success',
  agent: 'bg-warning/15 text-warning',
  system: 'bg-muted text-foreground',
};

// ---------------------------------------------------------------------------
// MessageBubble
// ---------------------------------------------------------------------------

interface MessageBubbleProps {
  message: HarnessChannelMessage;
  caseId: string | undefined;
}

/** Where the router sent an inbound message, relative to the case this harness is open on. */
function routingLabel(message: HarnessChannelMessage, caseId: string | undefined): string {
  if (message.routedToCaseId) {
    return message.routedToCaseId === caseId ? 'routed to this case' : 'routed to a different case';
  }
  return message.routingDecision === 'ignored' ? 'ignored by router' : 'awaiting router';
}

function MessageBubble({ message, caseId }: MessageBubbleProps) {
  // inbound = actor sent it (own message), outbound = agent replied
  const isOwn = message.direction === 'inbound';
  const isAgent = message.direction === 'outbound';

  if (isAgent) {
    return (
      <div
        data-testid={`message-bubble-${message.id}`}
        className="flex flex-col items-center gap-0.5 py-1"
        aria-label={`Agent message: ${message.content}`}
      >
        <Badge className={SENDER_BADGE['agent']} variant="secondary">
          agent
        </Badge>
        <p className="m-0 text-xs text-muted-foreground italic text-center px-4">
          {message.content}
        </p>
        <span className="text-[10px] text-muted-foreground/60">{formatTime(message.createdAt)}</span>
        {!['sent', 'delivered', 'read'].includes(message.status) && (
          <span role="status" className="text-[10px] font-medium text-destructive">
            not delivered: {message.status.replace(/_/g, ' ')}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={`message-bubble-${message.id}`}
      className={`flex flex-col gap-0.5 ${isOwn ? 'items-end' : 'items-start'}`}
    >
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-muted-foreground/60">
          {formatTime(message.createdAt)} · {routingLabel(message, caseId)}
        </span>
      </div>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isOwn
            ? 'bg-primary text-primary-foreground'
            : 'bg-accent/40 text-foreground border border-border'
        }`}
      >
        {message.content}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActorColumn
// ---------------------------------------------------------------------------

interface ActorColumnProps {
  caseId: string;
  config: ColumnConfig;
  messages: HarnessChannelMessage[];
  onMessageSent: () => void;
  headerAddon?: React.ReactNode;
}

function ActorColumn({ caseId, config, messages, onMessageSent, headerAddon }: ActorColumnProps) {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendStatus, setSendStatus] = useState<{ eventEmitted: boolean; note: string | null } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputId = `compose-${config.actorType}`;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Clear send status after 4s
  useEffect(() => {
    if (!sendStatus) return;
    const t = setTimeout(() => setSendStatus(null), 4000);
    return () => clearTimeout(t);
  }, [sendStatus]);

  const isDisabled = config.actor === null;

  async function handleSend() {
    if (!config.actor || !content.trim()) return;
    setSending(true);
    setSendError(null);
    setSendStatus(null);
    try {
      const result = await postChannelInbound({
        actorType: config.actorType,
        actorId: config.actor.id,
        content: content.trim(),
        caseId,
      });
      setContent('');
      setSendStatus({ eventEmitted: result.eventEmitted, note: result.note });
      onMessageSent();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div
      data-testid={`column-${config.actorType}`}
      role="region"
      aria-label={`${config.label} messages`}
      className="flex flex-col flex-1 min-w-0 border border-border rounded-lg bg-card overflow-hidden"
    >
      {/* Column header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border bg-accent/20 flex-shrink-0">
        <span className="font-semibold text-sm text-foreground truncate">
          {config.actor ? config.actor.name : config.label}
        </span>
        <Badge className={config.roleBadgeClass} variant="secondary">
          {config.label}
        </Badge>
        {isDisabled && !headerAddon && (
          <span className="text-xs text-muted-foreground italic ml-auto flex-shrink-0">
            Not assigned
          </span>
        )}
        {headerAddon && <div className="ml-auto flex-shrink-0">{headerAddon}</div>}
      </div>

      {/* Message list */}
      <div
        ref={listRef}
        aria-live="polite"
        aria-label={`${config.label} message history`}
        className="flex-1 overflow-y-auto p-3 flex flex-col gap-2 min-h-0"
      >
        {messages.length === 0 ? (
          <p className="m-0 text-xs text-muted-foreground text-center py-4">No messages yet</p>
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              caseId={caseId}
            />
          ))
        )}
      </div>

      {/* Compose box */}
      <div className="flex-shrink-0 border-t border-border p-3 flex flex-col gap-2 bg-background/50">
        <label htmlFor={inputId} className="sr-only">
          {config.label} message
        </label>
        <textarea
          id={inputId}
          data-testid={`compose-textarea-${config.actorType}`}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            isDisabled
              ? `${config.label} not assigned`
              : `Message as ${config.actor?.name ?? config.label}… (⌘↵ to send)`
          }
          disabled={isDisabled || sending}
          aria-label={`Compose message as ${config.label}`}
          aria-disabled={isDisabled}
          className="w-full px-2.5 py-2 text-sm border border-input rounded-md bg-background resize-none min-h-[64px] disabled:opacity-50 disabled:cursor-not-allowed"
          rows={3}
        />
        {sendError && (
          <p role="alert" className="m-0 text-xs text-destructive">
            {sendError}
          </p>
        )}
        {sendStatus && (
          <p
            role="status"
            className={`m-0 text-xs ${sendStatus.eventEmitted ? 'text-success' : 'text-warning'}`}
          >
            {sendStatus.eventEmitted
              ? 'Sent · agent event emitted'
              : (sendStatus.note ?? 'Sent · no event (no active router AgentKind found)')}
          </p>
        )}
        <div className="flex justify-end">
          <Button
            data-testid={`compose-send-${config.actorType}`}
            size="sm"
            onClick={handleSend}
            disabled={isDisabled || sending || !content.trim()}
            aria-label={`Send message as ${config.label}`}
            aria-disabled={isDisabled || sending || !content.trim()}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RepairTestHarnessPage
// ---------------------------------------------------------------------------

export function RepairTestHarnessPage() {
  const { id } = useParams<{ id: string }>();
  const [actors, setActors] = useState<HarnessActors | null>(null);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [channelMessages, setChannelMessages] = useState<Record<string, HarnessChannelMessage[]>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Initial data load — actors only
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);

    getHarnessActors(id)
      .then((actorsData) => {
        setActors(actorsData);
        setSelectedVendorId(actorsData.assignedVendorId ?? actorsData.vendors[0]?.id ?? null);
      })
      .catch((err) => {
        setLoadError(err instanceof Error ? err.message : 'Failed to load harness data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Fetch channel messages for all present actors
  const fetchAllChannelMessages = useCallback(async (currentActors: HarnessActors, vendorId: string | null) => {
    if (!id) return;
    const actorEntries: Array<[string, string]> = [
      ['manager', currentActors.landlord.id],
    ];
    if (currentActors.tenant) actorEntries.push(['tenant', currentActors.tenant.id]);
    if (vendorId) actorEntries.push(['vendor', vendorId]);

    const results = await Promise.allSettled(
      actorEntries.map(([, actorId]) => getChannelMessages(actorId, id))
    );

    setChannelMessages((prev) => {
      const next = { ...prev };
      results.forEach((result, i) => {
        const actorId = actorEntries[i]?.[1];
        if (actorId && result.status === 'fulfilled') {
          next[actorId] = result.value.messages;
        }
      });
      return next;
    });
  }, [id]);

  // Poll every 2s once actors are loaded
  useEffect(() => {
    if (!actors) return;

    // Immediate fetch
    fetchAllChannelMessages(actors, selectedVendorId);

    const interval = setInterval(() => {
      fetchAllChannelMessages(actors, selectedVendorId);
    }, 2000);

    return () => clearInterval(interval);
  }, [actors, selectedVendorId, fetchAllChannelMessages]);

  // Triggered immediately after a send to refresh messages faster
  const handleMessageSent = useCallback(() => {
    if (actors) {
      fetchAllChannelMessages(actors, selectedVendorId);
    }
  }, [actors, selectedVendorId, fetchAllChannelMessages]);

  const selectedVendor = actors?.vendors.find((v) => v.id === selectedVendorId) ?? null;

  // Build column configs once actors are loaded
  const columns: ColumnConfig[] = [
    {
      actorType: 'tenant',
      label: 'Tenant',
      roleBadgeClass: SENDER_BADGE['tenant'] ?? '',
      actor: actors?.tenant ?? null,
    },
    {
      actorType: 'manager',
      label: 'Landlord',
      roleBadgeClass: SENDER_BADGE['manager'] ?? '',
      actor: actors?.landlord ?? null,
    },
    {
      actorType: 'vendor',
      label: 'Handyman',
      roleBadgeClass: SENDER_BADGE['vendor'] ?? '',
      actor: selectedVendor,
    },
  ];

  return (
    <div
      data-testid="repair-test-harness-page"
      className="flex flex-col"
      style={{ height: 'calc(100dvh - var(--header-height, 56px))' }}
    >
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card flex-shrink-0">
        <Link
          to={id ? `/repairs/${id}` : '/repairs'}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Back to repair detail"
        >
          ← Back
        </Link>
        <h1 className="m-0 text-sm font-semibold text-foreground truncate">
          Repair Harness{id ? ` — #${id.slice(0, 8)}` : ''}
        </h1>
        <Badge
          variant="secondary"
          className="bg-warning/15 text-warning flex-shrink-0 ml-auto"
        >
          Test Harness
        </Badge>
      </div>

      {/* Error banner */}
      {loadError && (
        <div
          role="alert"
          className="px-4 py-2 bg-destructive/5 border-b border-destructive/20 text-destructive text-sm flex-shrink-0"
        >
          {loadError}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Loading harness…</p>
        </div>
      ) : (
        /* Three-column layout */
        <div className="flex flex-1 gap-3 p-3 min-h-0 overflow-hidden">
          {columns.map((col) => (
            <ActorColumn
              key={col.actorType}
              caseId={id ?? ''}
              config={col}
              messages={channelMessages[col.actor?.id ?? ''] ?? []}
              onMessageSent={handleMessageSent}
              headerAddon={
                col.actorType === 'vendor' && actors && actors.vendors.length > 1
                  ? (
                    <select
                      value={selectedVendorId ?? ''}
                      onChange={(e) => setSelectedVendorId(e.target.value || null)}
                      className="text-xs border border-input rounded px-1.5 py-0.5 bg-background text-foreground"
                      aria-label="Select vendor"
                      data-testid="vendor-selector"
                    >
                      {actors.vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}{v.id === actors.assignedVendorId ? ' (assigned)' : ''}
                        </option>
                      ))}
                    </select>
                  )
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default RepairTestHarnessPage;
